
import React, { useContext } from 'react';
import { KNOWLEDGE } from '../../../constants/knowledgeConstants';
import Tooltip from '@mui/material/Tooltip';
import { StyledFileCopyOutline, StyledHowToTranslate, StyledIconButton, StyledKBDetailHeader, StyledRating, StyledUseFul } from './kbDetailHeader.style';
import CalendarIcon from "../../../assets/icons/calendar-icon.svg";
import { ThemeContext } from 'styled-components';
import Typography from 'component/MarketPlace/Typography/Typography';
import { Box, Button, Skeleton, Stack, Theme, useMediaQuery } from '@mui/material';
import { RemoveRedEye } from '@mui/icons-material';
import { convertToShortNumber, getKbDate, handleKeyPress } from 'utils/functions';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';

export interface IKBDetailHeaderProp {
  kbDetail: any;
  locale: any;
  allowCopyToClipboard?: boolean;
  isShowPublishedDate?: boolean;
  viewCountConfig?: {
    isConvertViewCountToShortNumber?: boolean;
    isShowViewCountTooltip?: boolean; 
    isShowViewCountShortNumberSpacing?: boolean;
  };
  kbUsefulPercentage?: number;
  enableHowToTranslateButton?: boolean;
  howToTranslateAction?: () => void;
  isLoading?: boolean;
}

const KBDetailHeader: React.FC<IKBDetailHeaderProp> = (props) => {
  const {
    kbDetail,
    locale,
    allowCopyToClipboard = false,
    isShowPublishedDate,
    viewCountConfig = {},
    kbUsefulPercentage,
    enableHowToTranslateButton = false,
    howToTranslateAction,
    isLoading,
  } = props;
  const { isConvertViewCountToShortNumber, isShowViewCountTooltip, isShowViewCountShortNumberSpacing } = viewCountConfig;
  const kbType = kbDetail.type ? kbDetail.type.toUpperCase() : null;
  const isOutageArticles = kbType === KNOWLEDGE.OUTAGE;
  const theme = useContext(ThemeContext);
  const isSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const onClickCopy = () => {
    var copyText = window.location.href;
    navigator.clipboard.writeText(copyText);
  }

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="80%" height={48} />
        <Skeleton variant="rounded" width="50%" height={24} sx={{ mt: '26px' }} />
      </Box>
    );
  }

  return (
    <StyledKBDetailHeader className="kb-detail kb-detail-header kb-title">
      <div className="title">
        <Typography variant='h2' tabIndex={0} aria-label={(locale?.kbTitleLabel ?? "Knowledge Article Title") + " " + kbDetail.name} id="lbl-kbdetailheader-title">
          {kbDetail.name}
          {allowCopyToClipboard ? <Tooltip title={locale?.copyToClipboard} arrow placement="top-start">
            <StyledIconButton tabIndex={0} aria-label="copy" onClick={onClickCopy} onKeyDown={(e: any) => handleKeyPress(e, 'Enter', onClickCopy)}>
              <StyledFileCopyOutline />
            </StyledIconButton>
          </Tooltip> : <></>}
        </Typography>
      </div>

      <div className="inner">
        {/* date */}
        <span tabIndex={0} className="control">
          <img width="20px" src={CalendarIcon} alt="calendar" />
          <Typography marginLeft={`${theme.uptimeSpace[4]}px`} variant='body2' id="lbl-kbdetailheader-lastupdated">
            {getKbDate(kbDetail, !!isShowPublishedDate)}
          </Typography>
        </span>

        {/* rating */}
        {!isOutageArticles && (
          <StyledRating tabIndex={0} name="read-only" value={+kbDetail.rating} precision={0.5} readOnly id="img-kbdetailheader-ratingstars"/>
        )}

        {/* view count */}
        {!(isOutageArticles || isSmDown) && (
          <Stack direction="row" alignItems="center">
            <RemoveRedEye fontSize="small" sx={{ color: 'action.active' }} />
            <Tooltip title={isShowViewCountTooltip ? kbDetail.viewCount : ""} placement="top-start">
              <Typography tabIndex={0} variant="body2" color="text.secondary" ml="8px" id="lbl-kbdetailheader-viewcount">
                {isConvertViewCountToShortNumber ? convertToShortNumber(kbDetail.viewCount ?? 0, { spacing: isShowViewCountShortNumberSpacing }) : kbDetail.viewCount}
              </Typography>
            </Tooltip>
          </Stack>
        )}
        {!isOutageArticles && (
          <StyledUseFul tabIndex={0} direction="row" alignItems="center" isMobile={isSmDown}>
            <ThumbUpIcon fontSize="small" sx={{ color: 'action.active' }} />
            <Typography variant="body2" color="text.secondary" ml="8px" id="lbl-kbdetailheader-helpful">{kbUsefulPercentage}{isSmDown ? '%' : locale?.percentHelpfull}</Typography>
          </StyledUseFul>
        )}
        {/* how to translate button */}
        {(enableHowToTranslateButton && !isSmDown) && (
          <StyledHowToTranslate>
            <Button variant="text" size="small" onClick={howToTranslateAction}>
              {locale?.howToTranslate}
            </Button>
          </StyledHowToTranslate>
        )}
      </div>
    </StyledKBDetailHeader>
  );
};

KBDetailHeader.defaultProps = {
  kbDetail: {},
  locale: {
    commonStatus: {
      "Announcements": "Announcements",
      "OUTAGE": "OUTAGE",
      "ANNOUNCEMENTS": "ANNOUNCEMENTS",
      "SUPPORT ARTICLE": "SUPPORT ARTICLE",
    },
    copyToClipboard: "Copy to clipboard",
    percentHelpfull: "% find this article useful",
    kbTitleLabel: "Knowledge Article Title",
  },
  allowCopyToClipboard: false
};
export default KBDetailHeader;
